<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { Component } from 'vue';
import { useRoute } from 'vue-router';
import * as LucideIcons from 'lucide-vue-next';
import {
  CircleCheckBig,
  Home,
  Info,
  Building2,
  FileCheck,
  BookOpen,
  Megaphone,
  GraduationCap,
  Briefcase,
  Users,
  Quote,
} from 'lucide-vue-next';
import ModalContent from '@/components/ModalContent.vue';
import AccountAuthCard from '@/components/AccountAuthCard.vue';
import FormBlock from '@/components/blocks/FormBlock.vue';
import RichTextBlock from '@/components/blocks/RichTextBlock.vue';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth';
import {
  authSnapshotFromSession,
  authorisationRequirementMet,
  normaliseProcessBlockRequiredAuth,
  type ProcessBlockRequiredAuth,
} from '@/components/contentBlocks';
import { stripHtmlToText } from '@/utils/html';

const { t } = useI18n();

/** True when rich text has visible content (hides styled box for empty / tags-only HTML). */
function hasProcessScreenInfoRichText(html: string | undefined | null): boolean {
  return stripHtmlToText(html).length > 0;
}

/** Single process modal screen from API. */
export interface ProcessModalScreen {
  title: string;
  description: string;
  icon: string;
  info_richtext?: string;
  /** 'checklist' | 'warning'; controls the class on the info wrapper. Defaults to 'checklist'. */
  info_type?: 'checklist' | 'warning';
  document?: { id: number; url: string; title: string } | null;
  download_button_label?: string | null;
}

/** Final form payload from API (same shape as FormBlock form prop). */
export interface ProcessFinalForm {
  id?: number | null;
  intro?: string;
  thank_you_text?: string;
  file_mode: string;
  form_fields: Array<{
    clean_name: string;
    label: string;
    field_type: string;
    required: boolean;
    choices: string;
    default_value: string;
    help_text: string;
  }>;
  submission?: {
    mode: 'page_block';
    page_id?: number;
    block_id?: string;
  };
}

type Palette = { soft: string; strong: string; hoverStrong: string };

const DEFAULT_PALETTE: Palette = {
  soft: 'rgba(4, 120, 87, 0.1)',
  strong: '#047857',
  hoverStrong: '#036a4b',
};

const COLOUR_MAP: Record<string, Palette> = {
  primary: DEFAULT_PALETTE,
  secondary: {
    soft: 'rgba(124, 58, 237, 0.1)',
    strong: '#7c3aed',
    hoverStrong: '#6d28d9',
  },
  tertiary: {
    soft: 'rgba(234, 88, 12, 0.1)',
    strong: '#ea580c',
    hoverStrong: '#c2410c',
  },
  quaternary: {
    soft: 'rgba(8, 145, 178, 0.1)',
    strong: '#0891b2',
    hoverStrong: '#0e7490',
  },
  quinary: {
    soft: 'rgba(217, 119, 6, 0.1)',
    strong: '#d97706',
    hoverStrong: '#b45309',
  },
};

const BuildingIcon = (LucideIcons as Record<string, Component>).Building;
const MessageSquareIcon =
  (LucideIcons as Record<string, Component>).MessageSquare;
const FileTextIcon =
  (LucideIcons as Record<string, Component>).FileText ??
  (LucideIcons as Record<string, Component>).FileTextIcon;
const DownloadIcon = (LucideIcons as Record<string, Component>).Download;
const PenSquareIcon = (LucideIcons as Record<string, Component>).PenSquare;
const ClipboardCheckIcon =
  (LucideIcons as Record<string, Component>).ClipboardCheck;
const AwardIcon = (LucideIcons as Record<string, Component>).Award;

const PROCESS_ICON_MAP: Record<string, Component> = {
  'circle-check-big': CircleCheckBig,
  home: Home,
  info: Info,
  building: BuildingIcon ?? Building2,
  'building-2': Building2,
  'file-check': FileCheck,
  'file-text': FileTextIcon ?? FileCheck,
  'book-open': BookOpen,
  'message-square': MessageSquareIcon ?? Info,
  megaphone: Megaphone,
  'graduation-cap': GraduationCap,
  briefcase: Briefcase,
  users: Users,
  quote: Quote,
  download: DownloadIcon ?? FileCheck,
  'pen-square': PenSquareIcon ?? FileCheck,
  'clipboard-check': ClipboardCheckIcon ?? FileCheck,
  award: AwardIcon ?? FileCheck,
};

function iconComponent(iconName: string): Component | null {
  const key = iconName?.trim() ?? '';
  if (key && PROCESS_ICON_MAP[key]) return PROCESS_ICON_MAP[key];
  return PROCESS_ICON_MAP['file-text'] ?? null;
}

const props = withDefaults(
  defineProps<{
    title?: string;
    description?: string;
    link_text?: string;
    colour?: string;
    /** Who may open the flow; matches authorisation_required_message tiers, or none for public. */
    required_auth?: ProcessBlockRequiredAuth;
    screens?: ProcessModalScreen[];
    final_form?: ProcessFinalForm | null;
    /** Live page id (SPA) — required with streamBlockId for closing-form submission. */
    pageId?: number | null;
    /** Top-level stream block UUID from the pages API — required with pageId for submission. */
    streamBlockId?: string | null;
    /** Block-picker iframe: JSON matching pages API (`get_api_representation`). Query key `payload`. */
    payload?: string | null;
    preview_payload?: string | null;
    /**
     * Set when rendering inside Wagtail page preview (token in URL); bypasses required_auth so
     * editors can step through the flow. Also implied by `/block-preview/…` and `payload` query.
     */
    wagtailContentPreview?: boolean;
  }>(),
  {
    title: '',
    description: '',
    link_text: 'Begin',
    colour: 'primary',
    required_auth: 'none',
    screens: () => [],
    final_form: null,
    pageId: null,
    streamBlockId: null,
    wagtailContentPreview: false,
  }
);

const route = useRoute();

type ProcessPreviewPayload = {
  title?: string;
  description?: string;
  link_text?: string;
  colour?: string;
  required_auth?: ProcessBlockRequiredAuth;
  screens?: ProcessModalScreen[];
  final_form?: ProcessFinalForm | null;
};

const blockPickerPayloadRaw = computed(() => {
  const a = props.preview_payload?.trim();
  if (a) return a;
  return props.payload?.trim() ?? '';
});

const previewData = computed((): ProcessPreviewPayload | null => {
  const raw = blockPickerPayloadRaw.value;
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as unknown;
    if (p && typeof p === 'object') return p as ProcessPreviewPayload;
    return null;
  } catch {
    return null;
  }
});

/** Wagtail block-picker iframe or page preview with token: do not enforce viewer required_auth. */
const bypassProcessBlockAuth = computed(() => {
  if (props.wagtailContentPreview) return true;
  if (route.meta.blockPreview === true) return true;
  const q = route.query;
  return (
    typeof q.content_type === 'string' &&
    q.content_type.length > 0 &&
    typeof q.token === 'string' &&
    q.token.length > 0
  );
});

/** Merged view model: block-picker `payload` / `preview_payload` overrides discrete props when set. */
const title = computed(() =>
  (previewData.value?.title ?? props.title ?? '').trim()
);
const description = computed(() =>
  (previewData.value?.description ?? props.description ?? '').trim()
);
const linkText = computed(() =>
  (previewData.value?.link_text ?? props.link_text ?? 'Begin').trim()
);
const colour = computed(() =>
  (previewData.value?.colour ?? props.colour ?? 'primary').trim() || 'primary'
);
const requiredAuth = computed((): ProcessBlockRequiredAuth =>
  previewData.value != null
    ? normaliseProcessBlockRequiredAuth(previewData.value.required_auth)
    : normaliseProcessBlockRequiredAuth(props.required_auth)
);
const screens = computed((): ProcessModalScreen[] => {
  if (previewData.value?.screens != null) return previewData.value.screens;
  return props.screens ?? [];
});
const finalForm = computed((): ProcessFinalForm | null => {
  if (previewData.value && 'final_form' in previewData.value) {
    return previewData.value.final_form ?? null;
  }
  return props.final_form ?? null;
});

/** Closing step is file-only when there are no fields but uploads are enabled (matches backend file_mode). */
function closingFormHasFileUpload(f: ProcessFinalForm): boolean {
  const mode = f.file_mode?.trim() || 'no_files';
  return mode === 'one_file' || mode === 'multiple_files';
}

/** Closing step can submit with no fields when the block targets a process submission model. */
function closingFormHasSubmissionTarget(f: ProcessFinalForm): boolean {
  return f.submission?.mode === 'page_block';
}

/** Form payload passed to FormBlock, with page-bound submission when props allow it. */
const formForProcess = computed((): ProcessFinalForm | null => {
  const f = finalForm.value;
  if (!f) return null;
  const fields = Array.isArray(f.form_fields) ? f.form_fields : [];
  const hasFields = fields.length > 0;
  if (!hasFields && !closingFormHasFileUpload(f) && !closingFormHasSubmissionTarget(f)) {
    return null;
  }
  const merged: ProcessFinalForm = { ...f, form_fields: fields };
  const pid = props.pageId;
  const bid = props.streamBlockId?.trim();
  if (typeof pid === 'number' && bid) {
    return {
      ...merged,
      submission: { mode: 'page_block', page_id: pid, block_id: bid },
    };
  }
  return merged;
});

const hasClosingFormFields = computed(
  () => Array.isArray(finalForm.value?.form_fields) && finalForm.value!.form_fields.length > 0
);

/** Include upload-only closing forms (empty field list but file_mode requests files). */
const hasClosingFormStep = computed(() => {
  const f = finalForm.value;
  if (!f) return false;
  return (
    hasClosingFormFields.value ||
    closingFormHasFileUpload(f) ||
    closingFormHasSubmissionTarget(f)
  );
});

const canSubmitProcessForm = computed(() => {
  const inner = formForProcess.value;
  return (
    inner?.submission?.mode === 'page_block' &&
    typeof inner.submission.page_id === 'number' &&
    Boolean(inner.submission.block_id)
  );
});

const authStore = useAuthStore();
const authSnapshot = computed(() =>
  authSnapshotFromSession(authStore.isReady, authStore.isAuthenticated, authStore.currentUser)
);
const authModalOpen = ref(false);
const processModalOpen = ref(false);
const processStepIndex = ref(0);
const processFormSubmitting = ref(false);
const processFormSubmitted = ref(false);

const palette = computed(() => COLOUR_MAP[colour.value.trim()] ?? DEFAULT_PALETTE);

const processRequirementMet = computed(() => {
  if (bypassProcessBlockAuth.value) return true;
  const mode = requiredAuth.value;
  if (mode === 'none') return true;
  return authorisationRequirementMet(authSnapshot.value, mode);
});

const shouldOpenAuthModal = computed(
  () => requiredAuth.value !== 'none' && !processRequirementMet.value
);

const hasProcessFlow = computed(() => {
  const scr = screens.value;
  return scr.length > 0 || hasClosingFormStep.value;
});

const totalSteps = computed(() => {
  const scr = screens.value;
  const formStep = hasClosingFormStep.value ? 1 : 0;
  return scr.length + formStep;
});

const currentScreen = computed(() => {
  const scr = screens.value;
  const i = processStepIndex.value;
  if (i < 0 || i >= scr.length) return null;
  return scr[i];
});

const isFormStep = computed(() => {
  const scr = screens.value;
  return processStepIndex.value >= scr.length && totalSteps.value > 0;
});

const isFirstStep = computed(() => processStepIndex.value === 0);

const buttonLabel = computed(() =>
  shouldOpenAuthModal.value ? 'favór autentika' : (linkText.value || 'Begin')
);

const canOpenProcessModal = computed(
  () => !shouldOpenAuthModal.value && hasProcessFlow.value
);

/** Step indicator text for modal header (e.g. "Step 1 of 3"). Only when process has steps. */
const processStepIndicator = computed(() => {
  const total = totalSteps.value;
  if (total <= 0) return '';
  const current = processStepIndex.value + 1;
  return t('processFlow.stepOf', { current, total });
});

function onButtonClick() {
  if (shouldOpenAuthModal.value) {
    authModalOpen.value = true;
  } else if (hasProcessFlow.value) {
    processStepIndex.value = 0;
    processModalOpen.value = true;
  }
}

function closeAuthModal() {
  authModalOpen.value = false;
}

function closeProcessModal() {
  processModalOpen.value = false;
  processStepIndex.value = 0;
  processFormSubmitting.value = false;
  processFormSubmitted.value = false;
}

function goNext() {
  if (processStepIndex.value < totalSteps.value - 1) {
    processStepIndex.value += 1;
  }
}

function goBack() {
  if (processStepIndex.value > 0) {
    processStepIndex.value -= 1;
  }
}

/** Resolve document URL for download (same-origin or absolute). */
function documentHref(url: string | undefined): string {
  if (!url) return '#';
  if (/^https?:\/\//i.test(url)) return url;
  return url.startsWith('/') ? url : `/${url}`;
}

watch(processRequirementMet, (met) => {
  if (met && authModalOpen.value) {
    authModalOpen.value = false;
  }
});

watch(processModalOpen, (open) => {
  if (open) processStepIndex.value = 0;
});
</script>

<template>
  <section class="process-block">
    <div class="process-block__icon-wrap" :style="{ backgroundColor: palette.soft }">
      <component
        :is="FileTextIcon"
        class="process-block__icon"
        :style="{ color: palette.strong }"
        aria-hidden="true"
      />
    </div>

    <h3 class="process-block__title">{{ title }}</h3>
    <p class="process-block__description">{{ description }}</p>

    <button
      type="button"
      class="process-block__button"
      :style="{
        backgroundColor: palette.strong,
        '--process-block-button-hover': palette.hoverStrong,
        cursor: shouldOpenAuthModal || canOpenProcessModal ? 'pointer' : undefined,
      }"
      @click.prevent="onButtonClick"
    >
      {{ buttonLabel }}
    </button>

    <ModalContent
      v-if="requiredAuth !== 'none' && !bypassProcessBlockAuth"
      :open="authModalOpen"
      title="Account"
      @close="closeAuthModal"
    >
      <AccountAuthCard />
    </ModalContent>

    <ModalContent
      v-if="hasProcessFlow"
      :open="processModalOpen"
      :title="title"
      @close="closeProcessModal"
    >
      <template #header-subtitle>
        <span
          v-if="processStepIndicator && !processFormSubmitted"
          class="process-flow__step-indicator"
        >
          {{ processStepIndicator }}
        </span>
      </template>
      <div class="process-flow">
        <div
          v-if="totalSteps > 0 && !processFormSubmitted"
          class="process-flow__progress"
          role="status"
          aria-live="polite"
          :aria-label="processStepIndicator"
        >
          <div class="process-flow__progress-dots" aria-hidden="true">
            <span
              v-for="i in totalSteps"
              :key="i"
              class="process-flow__progress-dot"
              :class="{
                'process-flow__progress-dot--current': i === processStepIndex + 1,
                'process-flow__progress-dot--past': i < processStepIndex + 1,
              }"
            />
          </div>
        </div>
        <template v-if="isFormStep">
          <div class="process-flow__form-wrap">
            <FormBlock
              form-id="process-flow-final-form"
              :form="formForProcess ?? undefined"
              :hide-default-submit="true"
              @submitting="processFormSubmitting = $event"
              @submitted="processFormSubmitted = true"
            />
          </div>
          <div class="process-flow__nav">
            <button
              v-if="!processFormSubmitted"
              type="button"
              class="process-flow__btn process-flow__btn--back"
              @click="goBack"
            >
              {{ t('processFlow.back') }}
            </button>
            <button
              v-if="!processFormSubmitted"
              type="submit"
              form="process-flow-final-form"
              class="process-flow__btn process-flow__btn--submit"
              :disabled="processFormSubmitting || !canSubmitProcessForm"
              :style="{ backgroundColor: palette.strong, '--process-flow-next-hover': palette.hoverStrong }"
            >
              {{ processFormSubmitting ? t('processFlow.submitting') : t('processFlow.submitApplication') }}
            </button>
            <button
              v-if="processFormSubmitted"
              type="button"
              class="process-flow__btn process-flow__btn--close"
              :style="{ backgroundColor: palette.strong, '--process-flow-next-hover': palette.hoverStrong }"
              @click="closeProcessModal"
            >
              {{ t('processFlow.close') }}
            </button>
          </div>
        </template>
        <template v-else-if="currentScreen">
          <div class="process-flow__screen">
            <div
              class="process-flow__icon-wrap"
              :style="{ backgroundColor: palette.soft }"
            >
              <component
                :is="iconComponent(currentScreen.icon)"
                class="process-flow__icon"
                :style="{ color: palette.strong }"
                aria-hidden="true"
              />
            </div>
            <h4 class="process-flow__screen-title">{{ currentScreen.title }}</h4>
            <p class="process-flow__screen-desc">{{ currentScreen.description }}</p>
            <div
              v-if="hasProcessScreenInfoRichText(currentScreen.info_richtext)"
              :class="['process-flow__info', `process-flow__info--${currentScreen.info_type || 'checklist'}`]"
            >
              <RichTextBlock :html="currentScreen.info_richtext ?? ''" />
            </div>
            <div
              v-if="currentScreen.document?.url"
              class="process-flow__download-wrap"
            >
              <a
                :href="documentHref(currentScreen.document.url)"
                class="process-flow__btn process-flow__btn--download"
                target="_blank"
                rel="noopener noreferrer"
                download
              >
                {{ currentScreen.download_button_label || 'Download PDF Form' }}
              </a>
            </div>
          </div>
          <div class="process-flow__nav">
            <button
              v-if="!isFirstStep"
              type="button"
              class="process-flow__btn process-flow__btn--back"
              @click="goBack"
            >
              {{ t('processFlow.back') }}
            </button>
            <button
              v-if="processStepIndex < totalSteps - 1"
              type="button"
              class="process-flow__btn process-flow__btn--next"
              :style="{ backgroundColor: palette.strong, '--process-flow-next-hover': palette.hoverStrong }"
              @click="goNext"
            >
              {{ t('processFlow.next') }}
            </button>
          </div>
        </template>
      </div>
    </ModalContent>
  </section>
</template>

<style scoped>
.process-block {
  background-color: #fff;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  padding: 1rem;
  margin-bottom: 1rem;
  transition: box-shadow 0.3s ease, transform 0.3s ease;
}

.process-block:hover {
  box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
  transform: translateY(-4px);
}

.process-block__icon-wrap {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
}

.process-block__icon {
  width: 1.25rem;
  height: 1.25rem;
}

.process-block__title {
  margin: 0 0 0.5rem;
  color: #111827;
  font-size: 1rem;
  line-height: 1.4;
  font-weight: 600;
}

.process-block__description {
  margin: 0 0 1rem;
  color: #4b5563;
  font-size: 0.75rem;
  line-height: 1.55;
}

.process-block__button {
  width: 100%;
  border: 0;
  border-radius: 0.625rem;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.3;
  padding: 0.625rem 1rem;
  cursor: default;
  transition: transform 0.2s ease, background-color 0.2s ease;
}

.process-block__button:hover {
  background-color: var(--process-block-button-hover);
  transform: translateY(-1px);
}

.process-block__button:active {
  transform: translateY(0);
}

/* Step indicator in modal header (separate element under title) */
.process-flow__step-indicator {
  font-size: 0.875rem;
  line-height: 1.4;
  color: #6b7280;
  font-weight: 400;
}

/* Progress indicator: full width of modal panel with prototype spacing (px-6 pt-4). */
.process-flow__progress {
  width: calc(100% + 2 * 1.25rem);
  margin-left: -1.25rem;
  margin-right: -1.25rem;
  margin-top: -1.25rem;
  margin-bottom: 0;
  padding: 1rem 1.5rem 0.75rem;
  box-sizing: border-box;
}

@media (min-width: 640px) {
  .process-flow__progress {
    width: calc(100% + 2 * 1.5rem);
    margin-left: -1.5rem;
    margin-right: -1.5rem;
    margin-top: -1.5rem;
    padding: 1rem 1.5rem 0.75rem;
  }
}

.process-flow__progress-dots {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  width: 100%;
}

.process-flow__progress-dot {
  flex: 1;
  min-width: 0;
  height: 0.5rem;
  border-radius: 9999px;
  background: #e5e7eb;
  transition: background-color 0.2s ease;
}

.process-flow__progress-dot--past {
  background: #9ca3af;
}

.process-flow__progress-dot--current {
  background: #047857;
}

/* Process flow modal (stepper) */
.process-flow {
  min-height: 12rem;
}
.process-flow__screen {
  text-align: center;
  margin-bottom: 1.5rem;
}
.process-flow__icon-wrap {
  width: 5rem;
  height: 5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
}
.process-flow__icon {
  width: 2.5rem;
  height: 2.5rem;
}
.process-flow__screen-title {
  margin: 0 0 1rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
}
.process-flow__screen-desc {
  margin: 0 0 1.5rem;
  color: #4b5563;
  line-height: 1.6;
}
.process-flow__download-wrap {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 1.5rem;
}
.process-flow__info {
  background: #f9fafb;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  text-align: left;
}
/* Match prototype: no extra inner margin; box padding (p-6 mb-6) alone controls spacing */
.process-flow__info :deep(.block-rich-text) {
  margin: 0;
}

/* Checklist variant: match Prototype Licensing "Required Documents" (h5 → h2, CheckCircle on li) */
.process-flow__info--checklist :deep(.block-rich-text h2) {
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.75rem;
  font-size: 1rem;
}
.process-flow__info--checklist :deep(.block-rich-text ul) {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #4b5563;
}
.process-flow__info--checklist :deep(.block-rich-text ul li) {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding-left: 0;
  margin: 0;
}
.process-flow__info--checklist :deep(.block-rich-text ul li::before) {
  content: '';
  display: block;
  flex-shrink: 0;
  width: 1rem;
  height: 1rem;
  margin-top: 0.125rem;
  background-color: #047857;
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M22 11.08V12a10 10 0 1 1-5.93-9.14'/%3E%3Cpath d='m9 11 3 3L22 4'/%3E%3C/svg%3E");
  mask-size: contain;
  mask-repeat: no-repeat;
  mask-position: center;
  -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M22 11.08V12a10 10 0 1 1-5.93-9.14'/%3E%3Cpath d='m9 11 3 3L22 4'/%3E%3C/svg%3E");
  -webkit-mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
}

/* Warning variant: match Prototype Licensing "Important Instructions" (orange-50 box, FileText icon, bullet list) */
.process-flow__info--warning {
  background: #fff7ed;
  border: 1px solid #fed7aa;
}
.process-flow__info--warning :deep(.block-rich-text h2) {
  font-weight: 600;
  color: #7c2d12;
  margin: 0 0 0.75rem;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-left: 0;
}
.process-flow__info--warning :deep(.block-rich-text h2::before) {
  content: '';
  display: block;
  flex-shrink: 0;
  width: 1.25rem;
  height: 1.25rem;
  background-color: #7c2d12;
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z'/%3E%3Cpath d='M14 2v4a2 2 0 0 0 2 2h4'/%3E%3Cpath d='M10 9H8'/%3E%3Cpath d='M16 13H8'/%3E%3Cpath d='M16 17H8'/%3E%3Cpath d='M10 13H8'/%3E%3C/svg%3E");
  mask-size: contain;
  mask-repeat: no-repeat;
  mask-position: center;
  -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z'/%3E%3Cpath d='M14 2v4a2 2 0 0 0 2 2h4'/%3E%3Cpath d='M10 9H8'/%3E%3Cpath d='M16 13H8'/%3E%3Cpath d='M16 17H8'/%3E%3Cpath d='M10 13H8'/%3E%3C/svg%3E");
  -webkit-mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
}
.process-flow__info--warning :deep(.block-rich-text ul) {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #9a3412;
}
.process-flow__info--warning :deep(.block-rich-text ul li) {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding-left: 0;
  margin: 0;
}
.process-flow__info--warning :deep(.block-rich-text ul li::before) {
  content: '•';
  font-weight: 600;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.process-flow__form-wrap {
  margin-bottom: 1.5rem;
}
.process-flow__nav {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
  flex-wrap: wrap;
}
.process-flow__btn {
  border: 0;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-family: inherit;
}
.process-flow__btn--back {
  background: #e5e7eb;
  color: #374151;
}
.process-flow__btn--back:hover {
  background: #d1d5db;
}
.process-flow__btn--download {
  background: #7c3aed;
  color: #fff;
}
.process-flow__btn--download:hover {
  background: #6d28d9;
  color: #fff;
}
.process-flow__btn--next {
  margin-left: auto;
  color: #fff;
}
.process-flow__btn--next:hover {
  background: var(--process-flow-next-hover) !important;
}
.process-flow__btn--submit {
  margin-left: auto;
  color: #fff;
}
.process-flow__btn--submit:hover:not(:disabled) {
  background: var(--process-flow-next-hover) !important;
}
.process-flow__btn--submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
.process-flow__btn--close {
  flex: 1 1 100%;
  width: 100%;
  color: #fff;
}
.process-flow__btn--close:hover {
  background: var(--process-flow-next-hover) !important;
}

@media (min-width: 640px) {
  .process-block {
    border-radius: 1rem;
    padding: 1.5rem;
  }

  .process-block__icon-wrap {
    width: 3rem;
    height: 3rem;
    border-radius: 0.75rem;
    margin-bottom: 1rem;
  }

  .process-block__icon {
    width: 1.5rem;
    height: 1.5rem;
  }

  .process-block__title {
    font-size: 1.125rem;
  }

  .process-block__description {
    font-size: 0.875rem;
    margin-bottom: 1.5rem;
  }

  .process-block__button {
    border-radius: 0.75rem;
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
    font-size: 1rem;
  }
}
</style>
